import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ChevronLeft, ChevronRight, Dices, Gift } from "lucide-react";
import ProbabilitiesModal from "../components/ProbabilitiesModal";
import { FAMILY_LABEL } from "../lib/helpers";

function PackArt({ set }) {
  return (
    <div className="relative w-44 sm:w-52 aspect-[3/4.2] mx-auto rounded-[1.4rem] overflow-hidden shadow-[0_25px_45px_-18px_rgba(124,58,237,0.6)]"
      style={{ background: "linear-gradient(155deg,#8b5cf6 0%,#ec4899 55%,#38bdf8 100%)" }}>
      <div className="absolute inset-0 foil-sheen" />
      <div className="absolute inset-x-0 top-0 h-7 bg-white/20 border-b-2 border-dashed border-white/40" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-3 text-center">
        <Sparkles size={30} className="mb-2 opacity-90" />
        <div className="font-display font-extrabold text-2xl drop-shadow">{set.code}</div>
        <div className="text-[11px] font-bold mt-1 opacity-90 line-clamp-2">{set.name.split("[")[0]}</div>
      </div>
      <div className="absolute bottom-2 inset-x-0 text-center text-white/90 font-display font-bold text-sm tracking-widest">
        LUMACOFONO
      </div>
    </div>
  );
}

export default function SbustaScreen({ sets, state, onOpen, onGoBonus, dailyClaimedToday }) {
  const openable = useMemo(() => sets.filter((s) => s.openable), [sets]);
  const [idx, setIdx] = useState(0);
  const [showProb, setShowProb] = useState(false);

  if (openable.length === 0) {
    return <div className="p-6 text-center luma-muted">Caricamento espansioni…</div>;
  }

  const current = openable[Math.min(idx, openable.length - 1)];
  const pct = current.total_cards ? Math.round((current.owned_cards / current.total_cards) * 100) : 0;

  return (
    <div className="px-4 pt-4" data-testid="screen-sbusta">
      {/* Daily bonus quick access */}
      <button
        onClick={onGoBonus}
        data-testid="daily-bonus-access"
        className={`w-full luma-card !rounded-2xl p-3.5 flex items-center gap-3 text-left mb-4 ${
          !dailyClaimedToday ? "ring-2 ring-pink-300" : ""
        }`}
      >
        <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-pink-400 to-purple-500 text-white flex items-center justify-center shrink-0">
          <Gift size={22} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm">Bonus giornaliero</div>
          <div className="text-xs luma-muted font-semibold">
            {dailyClaimedToday ? "Già riscattato oggi · torna domani" : "Bustina bonus + carta collezione da riscattare!"}
          </div>
        </div>
        {!dailyClaimedToday && <span className="chip bg-pink-500 text-white">Riscatta</span>}
      </button>

      {/* Expansion selector */}
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setIdx((i) => (i - 1 + openable.length) % openable.length)} data-testid="prev-set-btn" className="pill-soft w-10 h-10 flex items-center justify-center shrink-0">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 overflow-x-auto no-scrollbar flex gap-2 px-0.5">
          {openable.map((s, i) => (
            <button
              key={s.set_id}
              onClick={() => setIdx(i)}
              data-testid={`set-chip-${s.code}`}
              className={`chip whitespace-nowrap ${i === idx ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" : "bg-white border-sky-200 luma-ink"}`}
            >
              {s.code}
            </button>
          ))}
        </div>
        <button onClick={() => setIdx((i) => (i + 1) % openable.length)} data-testid="next-set-btn" className="pill-soft w-10 h-10 flex items-center justify-center shrink-0">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Selected pack */}
      <motion.div key={current.set_id} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="luma-card p-5 text-center">
        <div className="text-[11px] font-bold uppercase tracking-widest luma-muted">{FAMILY_LABEL[current.family] || current.family}</div>
        <h2 className="font-display font-bold text-xl mt-0.5 leading-tight">{current.name.split("[")[0]}</h2>

        <div className="my-5">
          <PackArt set={current} />
        </div>

        <div className="max-w-xs mx-auto">
          <div className="flex items-center justify-between text-xs font-bold mb-1">
            <span className="luma-muted">Completamento</span>
            <span className="text-purple-600">{current.owned_cards}/{current.total_cards} · {pct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-sky-100 overflow-hidden">
            <motion.div className="h-full bg-gradient-to-r from-purple-500 to-pink-500" initial={{ width: 0 }} animate={{ width: `${pct}%` }} />
          </div>
        </div>

        <button
          onClick={() => onOpen(current)}
          data-testid="open-pack-button"
          className="pill-primary w-full mt-5 py-4 text-lg flex items-center justify-center gap-2"
        >
          <Sparkles size={20} /> Apri bustina
        </button>
        <p className="text-xs luma-muted mt-2 font-semibold">
          {current.cards_per_pack} carte a bustina · scorri sulla confezione per aprirla
        </p>

        <button onClick={() => setShowProb(true)} data-testid="show-probabilities-btn" className="mt-3 text-sm font-bold text-purple-600 inline-flex items-center gap-1.5">
          <Dices size={15} /> Vedi probabilità simulate
        </button>
      </motion.div>

      <p className="text-center text-xs luma-muted mt-4 font-semibold">3 aperture ogni ora · le bustine bonus non seguono il limite orario</p>

      {showProb && <ProbabilitiesModal setId={current.set_id} setName={current.name.split("[")[0]} onClose={() => setShowProb(false)} />}
    </div>
  );
}
